import React, { useState, useEffect, useMemo } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { type CronJob, type CronJobLog } from '../types';
import { callAxiosGet, callAxios } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([
  AllCommunityModule,
  ClientSideRowModelModule,
]);

const authLabels: Record<number, string> = { 0: 'NotUsed', 1: 'Dev', 2: 'Back', 3: 'ETF' };
const skipDayLabels: Record<number, string> = { [-1]: 'None', 0: 'SatSun', 1: 'SunMon', 2: 'Sun', 3: 'Weekly' };
const chanLabels: Record<number, string> = { 0: 'None', 1: 'Dev', 2: 'Oper', 3: 'Mngr' };
const runnerLabels: Record<number, string> = { 0: 'Main', 1: 'Second' };
const tyLabels: Record<number, string> = { [-1]: 'NotYet', 0: 'Done', 1: 'Error', 2: 'FinalFail', 3: 'UnKnownFunc' };

const cateOptions: { value: number; label: string }[] = [
  { value: 10, label: 'Open' }, { value: 30, label: 'Real' }, { value: 50, label: 'Close' },
  { value: 110, label: 'KRX' }, { value: 120, label: 'QW' }, { value: 130, label: 'BB' },
  { value: 170, label: 'Stat' }, { value: 190, label: 'Mkt' }, { value: 195, label: 'MktR' },
  { value: 210, label: 'USTK' }, { value: 211, label: 'USwap' }, { value: 220, label: 'UFUT' },
  { value: 231, label: 'KSwap' }, { value: 240, label: 'PosBs' }, { value: 250, label: 'Loan' },
  { value: 260, label: 'Ord' }, { value: 270, label: '\ub9c8\uac10' }, { value: 290, label: 'BO' },
  { value: 310, label: 'ETF' }, { value: 311, label: 'ETFFile' }, { value: 370, label: 'Check' },
  { value: 390, label: 'ETC' },
];

interface CronJobFormData {
  id: number;
  func: string;
  note: string;
  arg1: string;
  arg2: string;
  arg1desc: string;
  arg2desc: string;
  t0: string;
  t1: string;
  intv: number;
  repeat: boolean;
  skipDays: number;
  skipOnKorHoli: boolean;
  ord: number;
  skip: boolean;
  auth: number;
  chan: number;
  cate: number;
  rand: number;
  runner: number;
  devToo: boolean;
  eoyHoli: boolean;
}

const defaultFormData: CronJobFormData = {
  id: 0, func: '', note: '', arg1: '', arg2: '', arg1desc: '', arg2desc: '',
  t0: '09:00', t1: '16:00', intv: 60, repeat: false, skipDays: 0,
  skipOnKorHoli: true, ord: 0, skip: false, auth: 1, chan: 0, cate: 390,
  rand: 0, runner: 0, devToo: false, eoyHoli: false,
};

const formatTime = (t?: string): string => {
  if (!t) return '';
  // Handle TimeOnly format "HH:mm:ss"
  if (t.length <= 8 && t.includes(':')) return t.substring(0, 5);
  // Handle DateTime format
  try {
    const dt = new Date(t);
    return dt.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch { return t; }
};

const CronJobManagement: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [logs, setLogs] = useState<CronJobLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<CronJob | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CronJobFormData>({ ...defaultFormData });
  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  const fetchJobs = () => {
    setLoading(true);
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/cronjob/list',
      params: {},
      onSuccess: (data) => {
        setJobs((data as CronJob[]) || []);
        setError(null);
        setLoading(false);
      },
      onError: (errMsgs) => {
        setError(errMsgs?.join(', ') || 'CronJob 목록을 불러오는데 실패했습니다.');
        setJobs([]);
        setLoading(false);
      },
    });
  };

  // 행 선택만 (로그 패널 표시) — 수정 폼은 열지 않음
  const selectJob = (id: number) => {
    callAxiosGet({
      axiosInstance, m, logger,
      url: `/api/v1/cronjob/${id}`,
      params: {},
      onSuccess: (data) => {
        setSelectedJob(data as CronJob);
        fetchLogs(id);
      },
      onError: (errMsgs) => {
        setError(errMsgs?.join(', ') || 'CronJob 상세 정보를 불러오는데 실패했습니다.');
      },
    });
  };

  // 명시적 수정 (▶ 옆 ✎ 버튼) — 폼 채워서 펼침
  const openEditForm = (job: CronJob) => {
    setSelectedJob(job);
    setFormData({
      id: job.id,
      func: job.func,
      note: job.note || '',
      arg1: job.arg1 || '',
      arg2: job.arg2 || '',
      arg1desc: job.arg1desc || '',
      arg2desc: job.arg2desc || '',
      t0: formatTime(job.t0),
      t1: formatTime(job.t1),
      intv: job.intv,
      repeat: job.repeat,
      skipDays: job.skipDays,
      skipOnKorHoli: job.skipOnKorHoli,
      ord: job.ord,
      skip: job.skip,
      auth: job.auth,
      chan: job.chan,
      cate: job.cate,
      rand: job.rand || 0,
      runner: job.runner,
      devToo: job.devToo,
      eoyHoli: job.eoyHoli,
    });
    setShowForm(true);
    fetchLogs(job.id);
  };

  const fetchLogs = (id: number) => {
    callAxiosGet({
      axiosInstance, m, logger,
      url: `/api/v1/cronjob/${id}/logs`,
      params: {},
      onSuccess: (data) => {
        setLogs((data as CronJobLog[]) || []);
      },
      onError: () => {
        setLogs([]);
      },
    });
  };

  const createJob = () => {
    callAxios({
      axiosInstance, m, logger,
      url: '/api/v1/cronjob/create',
      params: {
        func: formData.func, note: formData.note || null,
        arg1: formData.arg1 || null, arg2: formData.arg2 || null,
        arg1desc: formData.arg1desc || null, arg2desc: formData.arg2desc || null,
        t0: formData.t0, t1: formData.t1, intv: formData.intv,
        repeat: formData.repeat, skipDays: formData.skipDays,
        skipOnKorHoli: formData.skipOnKorHoli, ord: formData.ord,
        skip: formData.skip, auth: formData.auth, chan: formData.chan,
        cate: formData.cate, rand: formData.rand || null,
        runner: formData.runner, devToo: formData.devToo, eoyHoli: formData.eoyHoli,
      },
      onSuccess: (data) => {
        const result = data as any;
        if (result.success) {
          m.alert('CronJob이 생성되었습니다.', 'success');
          setShowForm(false);
          fetchJobs();
        } else {
          setError(result.error || 'CronJob 생성에 실패했습니다.');
        }
      },
      onError: (errMsgs) => {
        setError(errMsgs?.join(', ') || 'CronJob 생성에 실패했습니다.');
      },
      isGet: false,
    });
  };

  const updateJob = () => {
    if (!selectedJob) return;
    callAxios({
      axiosInstance, m, logger,
      url: '/api/v1/cronjob/update',
      params: {
        id: formData.id, func: formData.func, note: formData.note || null,
        arg1: formData.arg1 || null, arg2: formData.arg2 || null,
        arg1desc: formData.arg1desc || null, arg2desc: formData.arg2desc || null,
        t0: formData.t0, t1: formData.t1, intv: formData.intv,
        repeat: formData.repeat, skipDays: formData.skipDays,
        skipOnKorHoli: formData.skipOnKorHoli, ord: formData.ord,
        skip: formData.skip, auth: formData.auth, chan: formData.chan,
        cate: formData.cate, rand: formData.rand || null,
        runner: formData.runner, devToo: formData.devToo, eoyHoli: formData.eoyHoli,
      },
      onSuccess: (data) => {
        const result = data as any;
        if (result.success) {
          m.alert('CronJob이 수정되었습니다.', 'success');
          setShowForm(false);
          setSelectedJob(null);
          fetchJobs();
        } else {
          setError(result.error || 'CronJob 수정에 실패했습니다.');
        }
      },
      onError: (errMsgs) => {
        setError(errMsgs?.join(', ') || 'CronJob 수정에 실패했습니다.');
      },
      isGet: false,
    });
  };

  const runNow = async (id: number, func: string) => {
    if (!(await m.confirm?.(`'${func}' 작업을 즉시 실행하시겠습니까?\n(시간/공휴일/Done 가드 무시)`))) return;
    const ajaxTimerId = m.showAjaxLoader();
    try {
      const response = await axiosInstance.post(`/api/v1/cronjob/run-now`, { id });
      m.hideAjaxLoader(ajaxTimerId);
      const r = response.data as { success: boolean; ty: number; elapsed: number; msg: string };
      const tyName = tyLabels[r.ty] || String(r.ty);
      const text = `${func} → ${tyName} (${r.elapsed.toFixed(1)}s)${r.msg ? `\n${r.msg}` : ''}`;
      m.alert(text, r.success ? 'success' : 'error');
      fetchJobs();
      if (selectedJob?.id === id) fetchLogs(id);
    } catch (err: any) {
      m.hideAjaxLoader(ajaxTimerId);
      setError(err?.response?.data?.error || err?.message || '실행 실패');
    }
  };

  const deleteJob = async (id: number) => {
    if (!(await m.confirm?.('정말 이 CronJob을 삭제하시겠습니까?'))) return;
    const ajaxTimerId = m.showAjaxLoader();
    try {
      const response = await axiosInstance.delete(`/api/v1/cronjob/${id}`);
      m.hideAjaxLoader(ajaxTimerId);
      const result = response.data as any;
      if (result.success) {
        m.alert('CronJob이 삭제되었습니다.', 'success');
        fetchJobs();
        if (selectedJob?.id === id) {
          resetForm();
          setShowForm(false);
        }
      } else {
        setError(result.error || 'CronJob 삭제에 실패했습니다.');
      }
    } catch (err: any) {
      m.hideAjaxLoader(ajaxTimerId);
      setError(err?.response?.data?.error || err?.message || 'CronJob 삭제에 실패했습니다.');
    }
  };

  const resetForm = () => {
    setFormData({ ...defaultFormData });
    setSelectedJob(null);
    setLogs([]);
    setShowForm(true);
  };

  useEffect(() => { fetchJobs(); }, []);

  const jobColumnDefs = useMemo<ColDef<CronJob>[]>(() => [
    {
      headerName: '',
      cellRenderer: (p: any) => (
        <span style={{ display: 'inline-flex', gap: '4px' }}>
          <button
            onClick={(e) => { e.stopPropagation(); runNow(p.data.id, p.data.func); }}
            title="즉시 실행 (시간/공휴일/Done 가드 무시)"
          >
            ▶
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); openEditForm(p.data); }}
            title="수정"
          >
            ✎
          </button>
        </span>
      ),
      sortable: false, filter: false,
    },
    { field: 'ord', headerName: 'ord' },
    { field: 'func', headerName: 'func', filter: 'agTextColumnFilter' },
    { field: 'note', headerName: 'note', filter: 'agTextColumnFilter' },
    { field: 't0', headerName: 't0', valueFormatter: p => formatTime(p.value) },
    { field: 't1', headerName: 't1', valueFormatter: p => formatTime(p.value) },
    { field: 'intv', headerName: 'intv(s)' },
    { field: 'skip', headerName: 'skip', cellRenderer: (p: any) => p.value ? 'Y' : '' },
    { field: 'auth', headerName: 'auth', valueFormatter: p => authLabels[p.value] || '' },
    { field: 'cate', headerName: 'cate', valueFormatter: p => cateOptions.find(c => c.value === p.value)?.label || String(p.value) },
    {
      field: 'ty', headerName: 'result',
      valueFormatter: p => p.value !== undefined && p.value !== null ? (tyLabels[p.value] || '') : '',
    },
    { field: 'elapsed', headerName: 'elapsed', valueFormatter: p => p.value != null ? `${p.value.toFixed(1)}s` : '' },
    { field: 'endT', headerName: 'endT', valueFormatter: p => formatTime(p.value) },
    { field: 'msg', headerName: 'msg', filter: 'agTextColumnFilter' },
  ], []);

  const logColumnDefs = useMemo<ColDef<CronJobLog>[]>(() => [
    { field: 'd', headerName: 'd' },
    { field: 'startT', headerName: 'startT', valueFormatter: p => formatTime(p.value) },
    { field: 'endT', headerName: 'endT', valueFormatter: p => formatTime(p.value) },
    { field: 'ty', headerName: 'ty', valueFormatter: p => tyLabels[p.value] || '' },
    {
      headerName: 'elapsed',
      valueGetter: (p) => {
        if (!p.data?.startT || !p.data?.endT) return null;
        return ((new Date(p.data.endT).getTime() - new Date(p.data.startT).getTime()) / 1000).toFixed(1);
      },
      valueFormatter: p => p.value != null ? `${p.value}s` : '',
    },
    { field: 'msg', headerName: 'msg', filter: 'agTextColumnFilter' },
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    flex: 0,
    minWidth: 50,
    resizable: true,
    sortable: true,
    cellStyle: { fontSize: '12px' },
  }), []);

  const inputStyle: React.CSSProperties = { width: '100%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#1e40af', marginBottom: '2px' };
  const btnStyle = (bg: string): React.CSSProperties => ({
    padding: '5px 12px', backgroundColor: bg, color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: '500',
  });

  if (loading) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ color: '#1e40af', fontSize: '12px', fontWeight: '500' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ margin: 0, color: '#1e40af', fontSize: '12px' }}>CronJob Management</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: '500' }}>{jobs.length}건</span>
          <button onClick={fetchJobs} style={btnStyle('#3b82f6')}>조회</button>
          <button onClick={resetForm} style={btnStyle('#22c55e')}>신규 등록</button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#fee2e2', border: '2px solid #ef4444', borderRadius: '4px', color: '#991b1b', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#991b1b', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
        </div>
      )}

      {/* Job Grid */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
        <div className="ag-theme-quartz" style={{ height: 350, width: '100%', fontSize: '12px' }}>
          <AgGridReact {...copyMenu.gridProps}
            rowData={jobs}
            columnDefs={jobColumnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            rowSelection="single"
            onRowClicked={(event) => { if (event.data) selectJob(event.data.id); }}
            getRowId={(p) => String(p.data.id)}
          />
        </div>
      </div>

      {/* Edit/Create Form */}
      {showForm && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#1e40af', fontSize: '12px' }}>
            {selectedJob ? `CronJob \uc218\uc815 (ID: ${selectedJob.id})` : 'CronJob \ub4f1\ub85d'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '10px' }}>
            <div>
              <label style={labelStyle}>func *</label>
              <input type="text" value={formData.func} onChange={e => setFormData({ ...formData, func: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>note</label>
              <input type="text" value={formData.note} onChange={e => setFormData({ ...formData, note: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>t0</label>
              <input type="time" value={formData.t0} onChange={e => setFormData({ ...formData, t0: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>t1</label>
              <input type="time" value={formData.t1} onChange={e => setFormData({ ...formData, t1: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>intv (sec)</label>
              <input type="number" value={formData.intv} onChange={e => setFormData({ ...formData, intv: parseInt(e.target.value) || 0 })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>ord</label>
              <input type="number" step="0.1" value={formData.ord} onChange={e => setFormData({ ...formData, ord: parseFloat(e.target.value) || 0 })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>auth</label>
              <select value={formData.auth} onChange={e => setFormData({ ...formData, auth: parseInt(e.target.value) })} style={inputStyle}>
                {Object.entries(authLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>cate</label>
              <select value={formData.cate} onChange={e => setFormData({ ...formData, cate: parseInt(e.target.value) })} style={inputStyle}>
                {cateOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>skipDays</label>
              <select value={formData.skipDays} onChange={e => setFormData({ ...formData, skipDays: parseInt(e.target.value) })} style={inputStyle}>
                {Object.entries(skipDayLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>chan</label>
              <select value={formData.chan} onChange={e => setFormData({ ...formData, chan: parseInt(e.target.value) })} style={inputStyle}>
                {Object.entries(chanLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>runner</label>
              <select value={formData.runner} onChange={e => setFormData({ ...formData, runner: parseInt(e.target.value) })} style={inputStyle}>
                {Object.entries(runnerLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>rand (sec)</label>
              <input type="number" value={formData.rand} onChange={e => setFormData({ ...formData, rand: parseInt(e.target.value) || 0 })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>arg1</label>
              <input type="text" value={formData.arg1} onChange={e => setFormData({ ...formData, arg1: e.target.value })} style={inputStyle} placeholder={formData.arg1desc || ''} />
            </div>
            <div>
              <label style={labelStyle}>arg2</label>
              <input type="text" value={formData.arg2} onChange={e => setFormData({ ...formData, arg2: e.target.value })} style={inputStyle} placeholder={formData.arg2desc || ''} />
            </div>
            <div>
              <label style={labelStyle}>arg1desc</label>
              <input type="text" value={formData.arg1desc} onChange={e => setFormData({ ...formData, arg1desc: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>arg2desc</label>
              <input type="text" value={formData.arg2desc} onChange={e => setFormData({ ...formData, arg2desc: e.target.value })} style={inputStyle} />
            </div>
          </div>
          {/* Checkboxes */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '10px', flexWrap: 'wrap' }}>
            {([
              ['repeat', 'repeat'], ['skip', 'skip'], ['skipOnKorHoli', 'skipOnKorHoli'],
              ['devToo', 'devToo'], ['eoyHoli', 'eoyHoli'],
            ] as [keyof CronJobFormData, string][]).map(([key, label]) => (
              <label key={key} style={{ fontSize: '12px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="checkbox"
                  checked={formData[key] as boolean}
                  onChange={e => setFormData({ ...formData, [key]: e.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>
          {/* Buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => { setShowForm(false); setSelectedJob(null); setLogs([]); }} style={btnStyle('#6b7280')}>취소</button>
            {selectedJob ? (
              <>
                <button onClick={() => deleteJob(selectedJob.id)} style={btnStyle('#ef4444')}>삭제</button>
                <button onClick={updateJob} style={btnStyle('#3b82f6')}>수정</button>
              </>
            ) : (
              <button onClick={createJob} style={btnStyle('#22c55e')}>등록</button>
            )}
          </div>
        </div>
      )}

      {/* Logs Grid */}
      {selectedJob && logs.length > 0 && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px' }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '12px' }}>
            {selectedJob.func} - Logs ({logs.length}건)
          </h3>
          <div className="ag-theme-quartz" style={{ height: 250, width: '100%', fontSize: '12px' }}>
            <AgGridReact {...copyMenu.gridProps}
              rowData={logs}
              columnDefs={logColumnDefs}
              defaultColDef={defaultColDef}
              animateRows={true}
              getRowId={(p) => String(p.data.id)}
            />
          </div>
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

export default CronJobManagement;
